/**
 * Genera slots de 15 min para hoy, desde "ahora" redondeado hacia arriba
 * hasta el cierre del día según horarios de la tienda (o 23:45 si no hay).
 *
 * @param {Array} dataOriginal - clientData.horarioRegular.dataOriginal (array de arrays por día)
 * @returns {string[]} slots "HH:mm"
 */
export function buildHoyTimeSlots (dataOriginal) {
  const now = new Date()
  const dayId = now.getDay() === 0 ? 7 : now.getDay()

  let openMinutes = 0
  let closeMinutes = 23 * 60 + 45

  const daySlots = Array.isArray(dataOriginal)
    ? dataOriginal.find(hr => Array.isArray(hr) && hr.some(i => i.Id === dayId))
    : null

  if (daySlots && daySlots.length > 0) {
    const ranges = daySlots
      .map((h) => ({
        desde: parseHoraToMinutes(h.HsDesde),
        hasta: parseHoraToMinutes(h.HsHasta)
      }))
      .filter(r => r.desde != null && r.hasta != null)

    if (ranges.length > 0) {
      openMinutes = Math.min(...ranges.map(r => r.desde))
      // Si cruza medianoche (hasta < desde), usamos 23:45 como tope de "hoy"
      closeMinutes = Math.max(...ranges.map(r => (r.hasta < r.desde ? 23 * 60 + 45 : r.hasta)))
    }
  }

  // Ahora redondeado a los próximos 15 min
  let start = now.getHours() * 60 + now.getMinutes()
  const rem = start % 15
  if (rem !== 0) start += (15 - rem)
  // Al menos 15 min en el futuro
  if (start <= now.getHours() * 60 + now.getMinutes()) start += 15

  start = Math.max(start, openMinutes)

  const slots = []
  for (let m = start; m <= closeMinutes; m += 15) {
    // Si hay rangos del día, solo incluir minutos que caen en algún rango
    if (daySlots && daySlots.length > 0) {
      const inRange = daySlots.some((h) => {
        const desde = parseHoraToMinutes(h.HsDesde)
        let hasta = parseHoraToMinutes(h.HsHasta)
        if (desde == null || hasta == null) return false
        if (hasta < desde) hasta = 23 * 60 + 45
        return m >= desde && m <= hasta
      })
      if (!inRange) continue
    }
    slots.push(minutesToHHmm(m))
  }
  return slots
}

function parseHoraToMinutes (hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null
  const parts = hhmm.split(':')
  if (parts.length < 2) return null
  const h = parseInt(parts[0], 10)
  const min = parseInt(parts[1], 10)
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null
  return h * 60 + min
}

function minutesToHHmm (m) {
  const h = Math.floor(m / 60)
  const min = m % 60
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

/**
 * Construye un Date local de hoy a la hora "HH:mm".
 */
export function todayAtHHmm (hhmm) {
  const parts = String(hhmm || '').split(':')
  const h = parseInt(parts[0], 10)
  const min = parseInt(parts[1], 10)
  const d = new Date()
  d.setSeconds(0, 0)
  d.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(min) ? min : 0, 0, 0)
  return d
}

/**
 * ISO local sin Z (ej. 2026-09-15T12:00:00).
 * Evita que JSON.stringify convierta a UTC (+3h en Argentina).
 */
export function todayAtHHmmLocalIso (hhmm) {
  const d = todayAtHHmm(hhmm)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
}

export default {
  buildHoyTimeSlots,
  todayAtHHmm,
  todayAtHHmmLocalIso
}
